import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  createSubAdmins,
  detailSubAdmin,
  listSubAdmins,
  updateSubAdmins,
} from "./callbacks";
import { ISubAdminPayload } from "../../../types/api/subAdmin";

export const useSubAdminCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<ISubAdminPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<ISubAdminPayload>,
    unknown,
    ISubAdminPayload
  > = {}
): UseMutationResult<
  TResponse<ISubAdminPayload>,
  unknown,
  ISubAdminPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<ISubAdminPayload>, unknown, ISubAdminPayload>({
    mutationFn: async (data: ISubAdminPayload) => {
      const response = await createSubAdmins(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.SUB_ADMINS_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const exists = old.data.find((v: any) => v._id === res.data._id);
          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useListSubAdminUsers = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<ISubAdminPayload[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponseWithQData<ISubAdminPayload[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<ISubAdminPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.SUB_ADMINS_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listSubAdmins(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useSubAdminDetail = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<ISubAdminPayload>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<ISubAdminPayload>, Error> => {
  return useQuery<TResponse<ISubAdminPayload>, Error>({
    queryKey: [API_ENDPOINTS.SUB_ADMINS_LIST, id],
    queryFn: async () => {
      const res = await detailSubAdmin(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const useSubAdminUpdate = (
  {
    qData = { page: 1, limit: 10 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<ISubAdminPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<ISubAdminPayload>,
    unknown,
    ISubAdminPayload
  > = {}
): UseMutationResult<
  TResponse<ISubAdminPayload>,
  unknown,
  ISubAdminPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<ISubAdminPayload>, unknown, ISubAdminPayload>({
    mutationFn: async (data: ISubAdminPayload) => {
      const response = await updateSubAdmins(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.SUB_ADMINS_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;

          const deleted = !!res.data.deletedAt;

          if (deleted) {
            return {
              ...old,
              data: old.data.filter((v: any) => v._id !== res.data._id),
            };
          }

          const exists = old.data.find((v: any) => v._id === res.data._id);

          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};


